import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShareLink } from "@secure-caseworker/shared";
import {
  CreateShareLinkModal,
  CreateShareLinkParams,
} from "../../components/ShareLinks/CreateShareLinkModal";
import { ShareLinkManager } from "../../components/ShareLinks/ShareLinkManager";
import Button from "../../components/common/ui/button/Button";
import { Download, Eye, Share2, FileText, FormInput } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { downloadFromResponse } from "../../utils/fileDownload";
import FileAuditLogs from "./components/FileAuditLogs";
import { FilePreviewModal } from "../../components/FilePreview/FilePreviewModal";
import { isPreviewable } from "../../utils/filePreview";

interface ResourceMetadata {
  id: string;
  name: string;
  type: "file" | "form_submission";
  uploadedBy?: string;
  submittedBy?: string;
  createdAt: string;
  size?: number;
  mimeType?: string;
  formTitle?: string;
}

export const ResourceDetailPage: React.FC = () => {
  const { fileId, submissionId } = useParams<{
    fileId?: string;
    submissionId?: string;
  }>();
  const navigate = useNavigate();

  const [resource, setResource] = useState<ResourceMetadata | null>(null);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<{
    isOpen: boolean;
    fileId: string | null;
    fileName: string | null;
    fileSize: number | null;
    mimeType: string | null;
  }>({
    isOpen: false,
    fileId: null,
    fileName: null,
    fileSize: null,
    mimeType: null,
  });

  // Determine resource type and ID from route
  const resourceType: "file" | "form_submission" = fileId
    ? "file"
    : "form_submission";
  const resourceId = fileId || submissionId;

  useEffect(() => {
    if (resourceId) {
      loadResourceDetails();
      loadShareLinks();
    }
  }, [resourceId]);

  const loadResourceDetails = async () => {
    try {
      setLoading(true);

      if (resourceType === "file") {
        const fileData = await apiClient.getFile(resourceId!);
        setResource({
          id: fileData.id,
          name: fileData.filename,
          type: "file",
          uploadedBy: fileData.uploadedBy,
          createdAt: fileData.uploadedAt,
          size: fileData.size,
          mimeType: fileData.mimeType,
        });
      } else {
        const submissionData = await apiClient.getFormSubmission(resourceId!);
        setResource({
          id: submissionData.id,
          name: `Form Submission`,
          type: "form_submission",
          submittedBy: submissionData.submittedBy ?? undefined,
          createdAt: submissionData.submittedAt,
          formTitle: submissionData.formTemplateTitle,
        });
      }
    } catch (err: any) {
      setError(err.message || "Failed to load resource");
    } finally {
      setLoading(false);
    }
  };

  const loadShareLinks = async () => {
    try {
      const data = await apiClient.listShareLinksForResource(
        resourceType,
        resourceId!
      );
      setShareLinks(data);
    } catch (err: any) {
      console.error("Failed to load share links:", err);
    }
  };

  const handleCreateShareLink = async (params: CreateShareLinkParams) => {
    const result = await apiClient.createShareLink({
      resourceType,
      resourceId,
      ...params,
    });

    await loadShareLinks();

    return {
      publicUrl: result.publicUrl,
      id: result.id,
    };
  };

  const handleRevokeShareLink = async (shareLinkId: string) => {
    await apiClient.revokeShareLink(shareLinkId);
    await loadShareLinks();
  };

  const handleDownload = async () => {
    if (resourceType === "file") {
      try {
        const response = await apiClient.downloadFileResponse(resourceId!);
        await downloadFromResponse(response, resource?.name || "download");
      } catch (error) {
        console.error("Download error:", error);
      }
    }
  };

  const handlePreview = () => {
    if (resourceType === "file" && resource) {
      setPreviewState({
        isOpen: true,
        fileId: resourceId || null,
        fileName: resource.name,
        fileSize: resource.size || null,
        mimeType: resource.mimeType || null,
      });
    }
  };

  const handleClosePreview = () => {
    setPreviewState({
      isOpen: false,
      fileId: null,
      fileName: null,
      fileSize: null,
      mimeType: null,
    });
  };

  const handleViewSubmission = () => {
    if (resourceType === "form_submission") {
      navigate(`/form-submissions/${resourceId}`);
    }
  };

  // Check if file is previewable
  const isFilePreviewable =
    resourceType === "file" &&
    resource?.mimeType &&
    resource?.size !== undefined &&
    isPreviewable(resource.mimeType, resource.size).canPreview;

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "Unknown";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  };

  if (loading) {
    return "Loading...";
  }

  if (error || !resource) {
    return "Error";
  }

  return (
    <div className="space-y-6">
      {/* Resource Metadata Card */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="p-3 bg-brand-100 dark:bg-brand-900/20 rounded-lg flex-shrink-0">
              {resourceType === "file" ? (
                <FileText className="h-8 w-8 text-brand-600" />
              ) : (
                <FormInput className="h-8 w-8 text-brand-600" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 break-words">
                {resourceType === "file"
                  ? resource.name
                  : resource.formTitle || "Form Submission"}
              </h1>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {resourceType === "file" ? (
                  <>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        Size:
                      </span>{" "}
                      <span className="text-gray-900 dark:text-gray-100">
                        {formatFileSize(resource.size)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        Type:
                      </span>{" "}
                      <span className="text-gray-900 dark:text-gray-100">
                        {resource.mimeType || "Unknown"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        Uploaded:
                      </span>{" "}
                      <span className="text-gray-900 dark:text-gray-100">
                        {new Date(resource.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        By:
                      </span>{" "}
                      <span className="text-gray-900 dark:text-gray-100">
                        {resource.uploadedBy || "Unknown"}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        Form:
                      </span>{" "}
                      <span className="text-gray-900 dark:text-gray-100">
                        {resource.formTitle || "Unknown"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        Submitted:
                      </span>{" "}
                      <span className="text-gray-900 dark:text-gray-100">
                        {new Date(resource.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        By:
                      </span>{" "}
                      <span className="text-gray-900 dark:text-gray-100">
                        {resource.submittedBy || "Unknown"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            {resourceType === "file" ? (
              <div className="flex gap-3">
                {isFilePreviewable && (
                  <Button
                    variant="outline"
                    size="md"
                    onClick={handlePreview}
                    startIcon={<Eye className="h-4 w-4" />}
                  >
                    Preview
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleDownload}
                  startIcon={<Download className="h-4 w-4" />}
                >
                  Download
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                size="md"
                onClick={handleViewSubmission}
                startIcon={<Eye className="h-4 w-4" />}
              >
                View Submission
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Share Links Section */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Share Links
          </h2>
          <Button
            variant="primary"
            size="md"
            onClick={() => setShowCreateModal(true)}
            startIcon={<Share2 className="h-4 w-4" />}
          >
            Create Share Link
          </Button>
        </div>

        <ShareLinkManager
          shareLinks={shareLinks}
          onRevoke={handleRevokeShareLink}
        />
      </div>

      {/* Audit Logs */}
      {resourceId && (
        <div className="mt-8">
          <FileAuditLogs resourceType={resourceType} resourceId={resourceId} />
        </div>
      )}

      {/* Create Share Link Modal */}
      <CreateShareLinkModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateShareLink}
        resourceType={resourceType}
      />

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={previewState.isOpen}
        onClose={handleClosePreview}
        fileId={previewState.fileId}
        fileName={previewState.fileName}
        fileSize={previewState.fileSize}
        mimeType={previewState.mimeType}
        onDownload={handleDownload}
      />
    </div>
  );
};
